import { BodyText, Button, Flex_unstable as Flex, Heading } from '@farol-ds/react'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { useDispatcher } from '@jusbrasil-web/shared-utils-events'
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'

import {
  DOCVIEW_APP_ID,
  DOCVIEW_ARTICLE_PAGE,
  SD_DOCVIEW_CHATS_EXPERIMENT_NAME,
  SD_DOCVIEW_CHATS_VARIANTS,
  SD_QUIZ_ACTION,
} from '../../constants'
import styles from './sd-quiz-sticky-cta.module.scss'

export interface SdQuizStickyCtaProps {
  quizRef: RefObject<HTMLDivElement>
  docId?: number | string
  title?: string
  description?: string
  ctaLabel?: string
  dvceType?: string
}

const PAGE = DOCVIEW_ARTICLE_PAGE
const APP_ID = DOCVIEW_APP_ID
const ACTION = SD_QUIZ_ACTION
const EXPERIMENT = SD_DOCVIEW_CHATS_EXPERIMENT_NAME
const VARIANT = SD_DOCVIEW_CHATS_VARIANTS.VARIANT_B

export function SdQuizStickyCta({
  quizRef,
  docId,
  title = 'Descubra o que fazer no seu caso',
  description = 'Responda algumas perguntas e entenda o que fazer na sua situação',
  ctaLabel = 'Começar',
  dvceType,
}: SdQuizStickyCtaProps) {
  const [isAnchored, setIsAnchored] = useState(false)
  const session = useUserSession()
  const { ref: dispatcherRef, sendMetrics } = useDispatcher<HTMLDivElement>()
  const sendMetricsRef = useRef(sendMetrics)
  sendMetricsRef.current = sendMetrics
  const stickyViewSent = useRef(false)
  const userId = session?.pid != null ? String(session.pid) : null

  const metadata = useMemo(
    () => ({
      page: PAGE,
      action: ACTION,
      position: 'sticky',
      type: 'redirect_to_quiz',
      doc_id: docId ?? null,
      experiment: EXPERIMENT,
      variant: VARIANT,
    }),
    [docId]
  )

  useEffect(() => {
    const target = quizRef.current
    if (!target || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0
        setIsAnchored(entry.intersectionRatio < 0.5 && scrolledPast)
      },
      { threshold: [0, 0.5, 1] }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [quizRef])

  useEffect(() => {
    if (isAnchored && !stickyViewSent.current) {
      sendMetricsRef.current('SeusDireitos.CtaView', userId, {
        app_id: APP_ID,
        page: PAGE,
        dvce_type: dvceType,
        metadata,
      })
      stickyViewSent.current = true
    }
  }, [isAnchored, metadata, userId, dvceType])

  const handleStart = () => {
    sendMetrics('SeusDireitos.CtaClick', userId, {
      app_id: APP_ID,
      page: PAGE,
      dvce_type: dvceType,
      metadata,
    })
    quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (!isAnchored) return null

  return (
    <div ref={dispatcherRef} className={styles.root}>
      <Flex direction="column" gap="3xs">
        <Flex direction="column" gap="6xs">
          <Heading size="md">{title}</Heading>
          <BodyText size="md">{description}</BodyText>
        </Flex>
        <Button kind="primary" size="md" block onClick={handleStart}>
          {ctaLabel}
        </Button>
      </Flex>
    </div>
  )
}
